import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAuth } from '../../state/AuthContext';
import { registerPasskey, passkeysSupported } from '../../lib/webauthn';
import type { TwoFactorSetup } from '../../lib/types';
import { LogoWordmark } from '../../components/Logo';
import { useT } from '../../i18n';

// Mandatory two-factor enrollment.
//
// Every Sansara account must enroll an authenticator app (TOTP) before it can
// reach the app. New signups land here automatically; existing accounts that
// predate mandatory 2FA are routed here at their next sign-in. The server
// enforces the same rule — until enrollment completes, all app/data API routes
// return 403 TWO_FACTOR_ENROLLMENT_REQUIRED.
//
// Flow: start setup (QR + secret) -> user scans in Authenticator/Authy ->
// confirm a 6-digit code -> server enables 2FA and returns 10 one-time backup
// codes -> user saves codes -> continue into the app.
export default function EnrollTwoFactor() {
  const navigate = useNavigate();
  const { session, csrfToken, refreshSession, logout } = useAuth();
  const t = useT();

  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [savedAck, setSavedAck] = useState(false);
  // Conv-57 R11-B — passkey offer on the completion step. Creating one here
  // (device supports it) means the NEXT sign-in is one Face ID tap instead of
  // password + authenticator. Skippable, error-tolerant: a failed passkey
  // never blocks entry — the account is already secured by TOTP.
  const [passkeyState, setPasskeyState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');
  const offerPasskey = passkeysSupported();

  // If the account is already enrolled, never show this screen.
  useEffect(() => {
    if (session?.twoFactorEnrolled) navigate('/app', { replace: true });
  }, [session?.twoFactorEnrolled, navigate]);

  // Begin enrollment once on mount (generate the secret + QR).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiPost<TwoFactorSetup>('/api/account/2fa/setup', { csrfToken });
        if (!cancelled) setSetup(data);
      } catch (err) {
        // If a secret was already started (e.g. page reload), re-fetch status and
        // re-issue a fresh setup so the QR is always present.
        if (!cancelled) {
          try {
            await apiGet('/api/account/2fa/status');
          } catch {
            /* ignore */
          }
          setError(err instanceof ApiError ? err.message : t('Could not start two-factor setup. Please retry.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await apiPost<{ backupCodes: string[] }>('/api/account/2fa/confirm', {
        code: code.trim(),
        csrfToken,
      });
      setBackupCodes(r.backupCodes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('That code was not valid. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    await refreshSession();
    navigate('/app', { replace: true });
  };

  const addPasskeyNow = async () => {
    setPasskeyState('busy');
    try {
      await registerPasskey(csrfToken ?? '', 'Passkey');
      setPasskeyState('done');
    } catch {
      // Same tolerance as Settings: a cancelled or failed ceremony is not an
      // error worth blocking entry over — the TOTP factor is already live.
      setPasskeyState('failed');
    }
  };

  const copyCodes = () => {
    if (backupCodes) navigator.clipboard?.writeText(backupCodes.join('\n')).catch(() => {});
  };

  const downloadCodes = () => {
    if (!backupCodes) return;
    const blob = new Blob(
      [
        `Sansara two-factor backup codes\nGenerated ${new Date().toISOString()}\n\n${backupCodes.join(
          '\n',
        )}\n\nEach code can be used once. Keep them somewhere safe.\n`,
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sansara-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const heading = useMemo(
    () => (backupCodes ? 'Save your backup codes' : 'Secure your account'),
    [backupCodes],
  );

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="card w-full max-w-md space-y-5 p-6">
        <div className="flex items-center justify-between">
          <LogoWordmark />
          <button type="button" className="text-sm text-ink-soft dark:text-slate-400 hover:underline" onClick={() => logout()}>
            {t('Sign out')}
          </button>
        </div>

        <div>
          <h1 className="text-xl font-semibold">{t(heading)}</h1>
          <p className="mt-1 text-sm text-ink-soft dark:text-slate-400">
            {backupCodes
              ? t('Store these one-time codes somewhere safe. Each can be used once if you lose your authenticator.')
              : t('Two-factor authentication is required for every Sansara account. Scan the QR code with Google Authenticator, Authy, or any TOTP app, then enter the 6-digit code.')}
          </p>
        </div>

        {error ? (
          <div className="rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-400">{t(error)}</div>
        ) : null}

        {/* Step 1 & 2: QR + confirm */}
        {!backupCodes ? (
          <>
            {setup ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <img
                    src={setup.qrDataUri}
                    alt={t('Two-factor QR code')}
                    className="h-44 w-44 rounded-lg bg-surface p-2"
                  />
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-center">
                  <p className="text-xs text-ink-soft dark:text-slate-400">{t('Can\'t scan? Enter this key manually:')}</p>
                  <p className="num mt-1 break-all text-sm font-semibold tracking-wide">{setup.secret}</p>
                </div>
                <form onSubmit={confirm} className="space-y-3">
                  <label className="label" htmlFor="totp">
                    {t('6-digit code from your app')}
                  </label>
                  <input
                    id="totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="input num w-full text-center text-lg tracking-[0.4em]"
                    placeholder="123456"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                  <button type="submit" className="btn btn-primary w-full" disabled={busy || code.length !== 6}>
                    {busy ? t('Verifying…') : t('Verify & enable')}
                  </button>
                </form>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="skeleton mx-auto h-44 w-44 rounded-lg" />
                <div className="skeleton h-10 w-full" />
              </div>
            )}
          </>
        ) : (
          /* Step 3: backup codes */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              {backupCodes.map((c) => (
                <span key={c} className="num text-center text-sm font-semibold tracking-wide">
                  {c}
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn btn-ghost flex-1" onClick={copyCodes}>
                {t('Copy')}
              </button>
              <button type="button" className="btn btn-ghost flex-1" onClick={downloadCodes}>
                {t('Download')}
              </button>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={savedAck}
                onChange={(e) => setSavedAck(e.target.checked)}
              />
              <span>{t('I have saved these backup codes somewhere safe.')}</span>
            </label>
            {offerPasskey && passkeyState !== 'done' ? (
              <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50 p-3 dark:border-brand-500/30 dark:bg-brand-500/10">
                <p className="text-sm font-semibold">{t('Skip the password next time')}</p>
                <p className="text-xs text-ink-soft dark:text-slate-400">
                  {t('Add a passkey — sign in with Face ID, Touch ID or your device unlock instead of your password and authenticator code.')}
                </p>
                <button
                  type="button"
                  className="btn btn-secondary w-full"
                  disabled={passkeyState === 'busy'}
                  onClick={addPasskeyNow}
                >
                  {passkeyState === 'busy' ? t('Please wait…') : t('Add a passkey')}
                </button>
                {passkeyState === 'failed' ? (
                  <p className="text-xs text-ink-faint dark:text-slate-400">
                    {t('Couldn’t add one now — you can add a passkey any time in Settings.')}
                  </p>
                ) : null}
              </div>
            ) : null}
            <button type="button" className="btn btn-primary w-full" disabled={!savedAck} onClick={finish}>
              {t('Continue to Sansara')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
